/* eslint-disable indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, Fragment } from 'react';

import Image from 'next/image';

import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Grid,
  List,
  Link,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Typography,
  IconButton,
  colors,
  useMediaQuery,
  Hidden,
  Chip,
  Paper,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PrintIcon from '@material-ui/icons/Print';

import PropTypes from 'prop-types';
import { useIntl, FormattedDate, FormattedTime, FormattedNumber } from 'react-intl';

import { IconAlternate } from '@/src/components/molecules';
import { InputCopyPaste } from '@/src/components/widgets';

const useStyles = makeStyles(theme => ({
  paymentMethod: {
    borderRadius: theme.spacing(0.5),
  },
  icon: {
    marginRight: theme.spacing(2),
  },
}));

// TODO: Migrate this to use the new dict in constants related to order status
export const ORDER_STATUS = {
  'order-created': {
    color: colors.blue,
    icon: 'fas fa-folder-plus',
  },
  'payment-pending': {
    color: colors.amber,
    icon: 'far fa-clock',
  },
  'payment-approved': {
    color: colors.green,
    icon: 'fas fa-dollar-sign',
  },
  'payment-denied': {
    color: colors.red,
    icon: 'fas fa-exclamation-circle',
  },
  handling: {
    color: colors.indigo,
    icon: 'fas fa-hand-paper',
  },
  invoice: {
    color: colors.blue,
    icon: 'fas fa-file-invoice-dollar',
  },
  invoiced: {
    color: colors.green,
    icon: 'fas fa-receipt',
  },
  delivered: {
    color: colors.green,
    icon: 'fas fa-truck',
  },
  'cancellation-requested': {
    color: colors.cyan,
    icon: 'far fa-question-circle',
  },
  cancel: {
    color: colors.cyan,
    icon: 'fas fa-times-circle',
  },
  canceled: {
    color: colors.red,
    icon: 'far fa-window-close',
  },
  reversed: {
    color: colors.indigo,
    icon: 'fas fa-history',
  },
};

export default function OrderDetails({ order, open, onClose }) {
  const classes = useStyles();
  const intl = useIntl();

  const [showPayments, setShowPayments] = useState('');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  });

  const { orderGroup = [] } = order || {};

  const handleShowPayments = orderNumber =>
    showPayments === orderNumber ? setShowPayments('') : setShowPayments(orderNumber);

  const handleClose = () => {
    setShowPayments('');
    onClose();
  };

  return (
    <Dialog
      fullScreen={isSm}
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title">
      <DialogTitle id="responsive-dialog-title" disableTypography sx={{ m: 0, p: 2 }}>
        <Typography variant="h6" component="div">
          Acompanhamento de pedido
        </Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          Informações detalhadas sobre os pedidos só poderão ser visualizadas através de e-mail e
          senha em nossa plataforma.
        </DialogContentText>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <List disablePadding>
            {orderGroup?.map((item, index) => (
              <Fragment key={item.orderNumber}>
                <ListItem divider={index < orderGroup.length - 1}>
                  <Hidden smDown>
                    <ListItemIcon className={classes.icon}>
                      <IconAlternate
                        fontIconClass={ORDER_STATUS[item.status]?.icon}
                        size={isSm ? 'small' : 'medium'}
                        color={ORDER_STATUS[item.status]?.color}
                      />
                    </ListItemIcon>
                  </Hidden>
                  <ListItemText disableTypography>
                    <Typography variant="body1" color="textPrimary">
                      {item.orderNumber} <Chip size="small" label={item.typeDisplay} />
                    </Typography>
                    <Typography variant="h6" component="div" color="textPrimary">
                      {item.statusDisplay}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" component="div">
                      <em>
                        atualizado em <FormattedDate value={item.dateUpdated} /> às{' '}
                        <FormattedTime value={item.dateUpdated} />{' '}
                        <Link
                          href={['/accounts/orders', item.orderNumber].join('/')}
                          target="_blank"
                          rel="noopener noreferrer">
                          (ver detalhes)
                        </Link>
                      </em>
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() => handleShowPayments(item.orderNumber)}
                      sx={{ cursor: 'pointer' }}>
                      <Typography variant="body1" color="primary">
                        Consultar pagamentos ({item.payments?.length}){' '}
                      </Typography>
                      {showPayments === item.orderNumber ? (
                        <ExpandLessIcon color="primary" />
                      ) : (
                        <ExpandMoreIcon color="primary" />
                      )}
                    </Box>
                  </ListItemText>
                </ListItem>
                {item.payments?.length > 0 &&
                  showPayments === item.orderNumber &&
                  item.payments.map(payment => (
                    <>
                      <ListItem
                        key={payment.uid}
                        divider={
                          !(payment.status === 'waiting' && payment.paymentMethod?.code === 'pix')
                        }
                        sx={{ paddingLeft: theme.spacing(4) }}>
                        <ListItemIcon>
                          <Image
                            src={payment.paymentMethod?.brand?.logo}
                            alt={payment.paymentMethod?.name}
                            width={40}
                            height={40}
                            className={classes.paymentMethod}
                          />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.disabled }}>
                              Venc. {intl.formatDate(payment.dueDate)} ({payment.statusDisplay})
                            </Typography>
                          }>
                          <Typography variant="body1">
                            {payment.label || payment.code}{' '}
                            <Chip
                              size="small"
                              label={
                                <FormattedNumber
                                  // eslint-disable-next-line react/style-prop-object
                                  style="currency"
                                  currency="BRL"
                                  value={payment.total}
                                />
                              }
                            />
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme.palette.action.active }}>
                            {payment.paymentMethod?.name}
                          </Typography>
                        </ListItemText>
                        {payment.status === 'waiting' &&
                          payment.paymentMethod?.code === 'billing-note' &&
                          payment.fundingInstrument?.link && (
                            <ListItemSecondaryAction>
                              <IconButton
                                component="a"
                                href={payment.fundingInstrument?.link}
                                target="_blank"
                                rel="noopener noreferrer">
                                <PrintIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                      </ListItem>
                      {payment.status === 'waiting' &&
                        payment.paymentMethod?.code === 'pix' &&
                        payment.fundingInstrument?.link && (
                          <ListItem divider sx={{ paddingTop: 0, paddingBottom: 0 }}>
                            <Grid container alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ margin: '0 auto', maxWidth: 200 }}>
                                  <Image
                                    src={payment.fundingInstrument?.link}
                                    alt="Pix QR Code"
                                    layout="responsive"
                                    width={200}
                                    height={200}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={8}>
                                <Box px={2}>
                                  <InputCopyPaste
                                    label="Pix copia e cola"
                                    size="medium"
                                    fullWidth
                                    value={payment.fundingInstrument?.code || ''}
                                  />
                                </Box>
                                <Box my={2} px={4}>
                                  <Typography variant="subtitle2" color="textSecondary">
                                    <strong>1</strong> - Acesse o aplicativo do seu banco abaixo
                                  </Typography>
                                  <Typography variant="subtitle2" color="textSecondary">
                                    <strong>2</strong> - Escolha pagar com Pix abaixo
                                  </Typography>
                                  <Typography variant="subtitle2" color="textSecondary">
                                    <strong>3</strong> - Utilize o QR Code ou cole a chave Pix
                                    abaixo
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </ListItem>
                        )}
                    </>
                  ))}
              </Fragment>
            ))}
          </List>
        </Paper>
        <DialogContentText component="div">
          Caso não tenha localizado seu pedido, verifique as informações que foram inseridas como
          e-mail ou CPF.
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}

OrderDetails.propTypes = {
  order: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
